import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaPlatform,
} from '@wix/panorama-client-web';
import type { PanoramaClient } from '@wix/panorama-client-web';

const FULL_ARTIFACT_ID = 'com.wixpress.users.login-react-app';
const COMPONENT_ID = 'login-react-app';

type PanoramaTransaction = ReturnType<PanoramaClient['transaction']>;
/** Optional per-transaction custom data, forwarded to Panorama start/finish. */
type TransactionData = Parameters<PanoramaTransaction['finish']>[0];

/** Webpack's runtime public path; only defined inside a webpack bundle. */
declare const __webpack_public_path__: string | undefined;

const STATICS_VERSION_PATTERN = /\/services\/[^/]+\/(\d+\.\d+\.\d+)\//;
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

/**
 * The `src` of the script currently being evaluated — i.e. this bundle —
 * captured at module load, the only time `document.currentScript` is set.
 * Guarded so importing this module on the server is a no-op. It can't go
 * through the LoginEnvironment port: no env exists yet at module load.
 */
/* eslint-disable no-restricted-globals */
const bundleScriptSrc: string | undefined =
  typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)?.src || undefined
    : undefined;
/* eslint-enable no-restricted-globals */

function getWebpackPublicPath(): string | undefined {
  try {
    return typeof __webpack_public_path__ === 'string'
      ? __webpack_public_path__
      : undefined;
  } catch {
    return undefined;
  }
}

/** Extracts "1.2.3" from `…/services/<artifact>/1.2.3/…`. */
export function parseStaticsVersion(url?: string): string | undefined {
  return url?.match(STATICS_VERSION_PATTERN)?.[1];
}

export interface ArtifactVersionSources {
  publicPath?: string;
  scriptSrc?: string;
  staticsUrl?: string;
  buildArtifactVersion?: string;
}

/**
 * Panorama needs the exact deployed semver (e.g. "1.2872.0") for
 * gradual-rollout attribution: the "…Success Rate Delta" rollout rule matches
 * `artifact_version` against the from/to versions being rolled out. Sources,
 * in order:
 * 1. The URL the bundle itself was loaded from (webpack public path, then the
 *    bundle's script src). Independent of the HTML template, so it works for
 *    the Scala `index.vm` and the Node-rendered `index.ejs` pages alike.
 * 2. The render model's `staticsUrl` (`window.__STATICS_URL__`), when set.
 * 3. The build-time `process.env.ARTIFACT_VERSION`, only if it is a semver —
 *    in CI yoshi sets it to the build fingerprint (a hash), which the rollout
 *    judge can't match.
 * Local/dev builds (no versioned statics URL) yield undefined.
 */
export function resolveArtifactVersion({
  publicPath = getWebpackPublicPath(),
  scriptSrc = bundleScriptSrc,
  staticsUrl,
  buildArtifactVersion = process.env.ARTIFACT_VERSION,
}: ArtifactVersionSources = {}): string | undefined {
  return (
    parseStaticsVersion(publicPath) ??
    parseStaticsVersion(scriptSrc) ??
    parseStaticsVersion(staticsUrl) ??
    (SEMVER_PATTERN.test(buildArtifactVersion ?? '')
      ? buildArtifactVersion
      : undefined)
  );
}

function createPanoramaClient(staticsUrl?: string): PanoramaClient | undefined {
  // Client creation wires up plugins (sentry, environment, …); guard it so a
  // monitoring-setup failure can never break app init in the RootStore ctor.
  try {
    return panoramaClientFactory({
      baseParams: {
        fullArtifactId: FULL_ARTIFACT_ID,
        artifactVersion: resolveArtifactVersion({ staticsUrl }),
        platform: PanoramaPlatform.Standalone,
      },
    })
      .withGlobalConfig(createGlobalConfig())
      .client({ baseParams: { componentId: COMPONENT_ID } });
  } catch {
    return undefined;
  }
}

/**
 * Panorama replaces the deprecated FedOps logger for this app. Component
 * transactions map 1:1 to the old FedOps interactions (`startTransaction` ↔
 * `interactionStarted`, `finishTransaction` ↔ `interactionEnded`) and the
 * component-load transaction replaces `appLoaded`. They also power the
 * Deployments-NG "Safer gradual rollout" gate ("Panorama Component Transaction
 * Success Rate Delta"). A transaction is a success once `finish()` is called;
 * a started-but-unfinished transaction is counted as a failure.
 */
export class PanoramaService {
  private readonly client: PanoramaClient | undefined;
  private readonly openTransactions = new Map<string, PanoramaTransaction>();

  /** @param staticsUrl the render model's statics URL, a version fallback. */
  constructor(staticsUrl?: string) {
    this.client = createPanoramaClient(staticsUrl);
  }

  /** Starts the component-load transaction (page-load success rate). */
  reportLoadStart(): void {
    this.client?.reportLoadStart();
  }

  /** Marks the component load as successful. */
  reportLoadFinish(): void {
    this.client?.reportLoadFinish();
  }

  startTransaction(name: string, data?: TransactionData): void {
    const transaction = this.client?.transaction(name);
    if (transaction) {
      this.openTransactions.set(name, transaction);
      transaction.start(data);
    }
  }

  finishTransaction(name: string, data?: TransactionData): void {
    this.openTransactions.get(name)?.finish(data);
    this.openTransactions.delete(name);
  }
}
